import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { enumEntrySummaryType } from '@data-layer/enums/entry-summaries';
import { IsfEstimatedInfoModel, IsfModel } from '@data-layer/models/isf';
import { CustomState, enumBondRequestState, enumBondType, enumWeightUnit } from '@data-layer/enums/common';
import { enumIsfShipmentCode, enumIsfShipmentType } from '@data-layer/enums/isf';
import { CompanyModel } from '@data-layer/models/companies';
import { BondModel, BondRequestModel, PortModel, ValidationMessageModel } from '@data-layer/models/common';
import { IsfHelper } from '@helpers/isf.helper';
import { EntityModel } from '@data-layer/models/entities';
import { CodeRepository } from '@data-layer/repositories';
import { DESC_ENTITY } from '@constants/desc-entity';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import {
  CHINA_COUNTRY_CODE,
  ENTERED_VALUE_CHINA_LIMIT,
  ENTERED_VALUE_TOTAL_LIMIT,
  WEIGHT_UNIT_KG,
  WEIGHT_UNIT_LB,
} from '@constants/constants';
import { flatten } from 'lodash-es';
import { take } from 'rxjs/operators';
import { InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';

@Component({
  selector: 'desktop-isf-summary-shipment-details',
  templateUrl: './desktop-summary-shipment-details.component.html',
  styleUrls: ['./desktop-summary-shipment-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopSummaryShipmentDetailsComponent implements OnChanges {
  @Input() public isf: IsfModel;
  @Input() public company: CompanyModel;
  @Input() public isEditMode: boolean;
  @Input() public isShowBR: boolean;
  @Output() public edit: EventEmitter<{ id: number; entityType: enumEntityModelCode }> = new EventEmitter<{
    id: number;
    entityType: enumEntityModelCode;
  }>();
  @Output() public editShipmentType: EventEmitter<InvoiceModel[]> = new EventEmitter<InvoiceModel[]>();
  @Output() public editBL: EventEmitter<void> = new EventEmitter<void>();

  public enumEntrySummaryType: typeof enumEntrySummaryType = enumEntrySummaryType;
  public enumBondType: typeof enumBondType = enumBondType;
  public enumIsfShipmentCode: typeof enumIsfShipmentCode = enumIsfShipmentCode;
  public enumBondRequestState: typeof enumBondRequestState = enumBondRequestState;
  public enumIsfShipmentType: typeof enumIsfShipmentType = enumIsfShipmentType;
  public bond: BondModel;
  public bondRequest: BondRequestModel;
  public arrivalPort: PortModel;
  public info = DESC_ENTITY.BN;
  public estimatedIsfInfo: IsfEstimatedInfoModel;
  public enumWeightUnit: typeof enumWeightUnit = enumWeightUnit;
  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public WEIGHT_UNIT_KG: string = WEIGHT_UNIT_KG;
  public WEIGHT_UNIT_LB: string = WEIGHT_UNIT_LB;
  public isChina: boolean = false;
  public enteredValueLimit: number = ENTERED_VALUE_TOTAL_LIMIT;

  constructor(private codeRepository: CodeRepository) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.isf) {
      this.estimatedIsfInfo = IsfHelper.getEstimatedInformation(this.isf);
      this.bond = IsfHelper.getBond(this.isf);
      this.bondRequest = IsfHelper.getBondRequest(this.isf);
      this.getPort();
      this.initEnteredValueLimit();
    }
  }

  public get isContinuous(): boolean {
    return this.bond.type === enumBondType.CONTINUOUS;
  }

  public get bondRequestPortCodeValidationText(): string {
    if (!this.bondRequest || !this.bondRequest.portCode) {
      return `BACK_ERROR.MISSING_PORT_CODE`;
    }
    return null;
  }

  public get importerIdentifier(): string {
    return this.importer ? this.importer.entityIdentifier : null;
  }

  public get importerIdentifierQual(): string {
    return this.importer ? 'IDENTIFIER_QUALIFIER.' + this.importer.entityIdentifierQualifier : null;
  }

  public get importer(): EntityModel {
    return this.isf.importer;
  }

  public getValidationText(field: string): string {
    if (!this.bond || !this.bond.validationMessages || !this.bond.validationMessages[field] || !this.bond.validationMessages[field].text) {
      return null;
    }
    return `BACK_ERROR.${this.bond.validationMessages[field].text}`;
  }

  public get hasAnyBill(): boolean {
    return !!this.isf?.shipment?.masterBill?.carrier || !!this.isf?.shipment?.houseBill?.carrier;
  }

  public get hasAnyBillNumber(): boolean {
    return !!this.isf?.shipment?.masterBill?.number || !!this.isf?.shipment?.houseBill?.number;
  }

  public get isMasterBill(): boolean {
    return !!this.isf?.shipment?.masterBill?.number;
  }

  public get carrierNumber(): string {
    return this.isf?.shipment?.masterBill?.carrier || this.isf?.shipment?.houseBill?.carrier;
  }

  public get billNumber(): string {
    return this.isf?.shipment?.masterBill?.number || this.isf?.shipment?.houseBill?.number;
  }

  public hasEstimationValidationText(field: string): boolean {
    if (this.isf?.estimatedInformation?.[field] !== null) {
      return false;
    }
    return true;
  }

  public get hasEstimationInvalid(): boolean {
    const errors: string[] = (this.isf?.estimatedInformation?.validationMessages || []).map((item: ValidationMessageModel) => item.text);
    if (errors.includes('ESTIMATED_VALUE_TOO_LARGE')) {
      return true;
    }
    return false;
  }

  public get hasShipmentError(): boolean {
    if (this.isf?.type === enumIsfShipmentType.FORMAL && !this.isf?.bondInfo && !this.isf?.bondRequest) {
      return true;
    }

    if (this.isf?.type === enumIsfShipmentType.INFORMAL && !this.isf?.estimatedInformation) {
      return true;
    }

    return false;
  }

  public get getMaxText(): string {
    return 'BACK_ERROR.INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP';
  }

  public get singleBondText(): string {
    if (!this.bondRequest?.customState?.header) {
      return this.info.purchaseSingleBondRequested;
    }

    switch (this.bondRequest?.customState?.header) {
      case CustomState.CANCELED:
        return this.info.purchaseSingleBondCanceled;
      case CustomState.CANCELING:
        return this.info.purchaseSingleBondCanceling;
      case CustomState.REFUNDED:
        return this.info.purchaseSingleBondRefunded;
      default:
        return this.info.purchaseSingleBondRequested;
    }
  }

  private initEnteredValueLimit(): void {
    const lines: LineModel[] = flatten((this.isf?.shipment?.invoices || []).map((invoice: InvoiceModel) => invoice?.lines));
    this.isChina = lines.some((line: LineModel) => line.countryOfOrigin?.code === CHINA_COUNTRY_CODE);
    this.enteredValueLimit = this.isChina ? ENTERED_VALUE_CHINA_LIMIT : ENTERED_VALUE_TOTAL_LIMIT;
  }

  private getPort(): void {
    if (this.bondRequest && this.bondRequest.portCode) {
      this.codeRepository
        .getDomesticPort(this.bondRequest.portCode)
        .pipe(take(1))
        .subscribe((port: PortModel) => {
          this.arrivalPort = port;
        });
    }
  }
}
